import { DocumentIcon } from "@heroicons/react/24/outline";
import { Badge } from "@mantine/core";

import numeral from "numeral";
import HoldOfferActions from "./components/HoldOfferActions";

export const getHoldOfferColumns = (refresh?: () => void, data?: any[]) => {
  // Check if any offer has no decision (needs action buttons)
  const hasAnyPendingOffers = data?.some((offer: any) => {
    const holdParticipant = offer?.hold_kava_participant;
    const decision = holdParticipant?.decision;
    return !holdParticipant || !decision;
  });

  const columns = [
    {
      accessor: "",
      name: "Policy Number | Insured",
      render: (offer: any) => (
        <div className="flex items-center max-w-md">
          <div className="h-10 w-10 flex-shrink-0">
            <DocumentIcon className="h-10 w-10 rounded-full" />
          </div>
          <div className="ml-4 break-all pl-3 truncate text-ellipsis">
            <div className="font-medium truncate text-gray-900">
              {offer?.offer_detail?.insured_by || "N/A"}
            </div>
            <div className="text-gray-500">
              {offer?.offer_detail?.policy_number || "N/A"}
            </div>
          </div>
        </div>
      ),
    },
    {
      accessor: "",
      name: "Business Name",
      render: (offer: any) => {
        const isPlaceItOffer = offer?.offer_from?.toLowerCase() === "placeit";
        const businessName = !isPlaceItOffer
          ? offer?.class_of_business?.business_name
          : offer?.classofbusiness?.business_name;
        return (
          <div className="flex items-center">
            <div className="font-medium text-gray-900">
              {businessName || "N/A"}
            </div>
          </div>
        );
      },
    },
    {
      accessor: "",
      name: "Policy Period",
      render: (offer: any) => (
        <div className="flex items-center">
          <div>
            <div className="font-medium text-green-900">
              {offer?.offer_detail?.period_of_insurance_from
                ? new Date(
                    offer.offer_detail.period_of_insurance_from
                  ).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })
                : "N/A"}
            </div>
            <div className="text-red-500">
              {offer?.offer_detail?.period_of_insurance_to
                ? new Date(
                    offer.offer_detail.period_of_insurance_to
                  ).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })
                : "N/A"}
            </div>
          </div>
        </div>
      ),
    },
    {
      accessor: "",
      name: "Premium | Sum Insured",
      render: (offer: any) => (
        <div className="flex items-center max-w-md">
          <div className="break-all pl-3 truncate text-ellipsis">
            <div className="font-medium truncate text-gray-900">
              {offer?.offer_detail?.currency}{" "}
              {offer?.premium ? numeral(offer.premium).format("#,#.##") : "N/A"}
            </div>
            <div className="text-gray-500">
              {offer?.sum_insured
                ? numeral(offer.sum_insured).format("#,#.##")
                : "N/A"}
            </div>
          </div>
        </div>
      ),
    },
    {
      accessor: "",
      name: "Fac Offer",
      render: (offer: any) => (
        <div className="flex items-center">
          <div className="font-medium text-gray-900">
            {offer?.facultative_offer ? `${offer.facultative_offer}%` : "N/A"}
          </div>
        </div>
      ),
    },
    {
      accessor: "",
      name: "Status",
      render: (offer: any) => {
        const decision = offer?.hold_kava_participant?.decision;

        if (!decision) {
          return <Badge color="yellow">Pending</Badge>;
        }

        const isAccepted = decision?.toUpperCase() === "ACCEPT";
        const isDeclined = decision?.toUpperCase() === "DECLINE";

        return (
          <Badge color={isAccepted ? "green" : isDeclined ? "red" : "gray"}>
            {isAccepted ? "Accepted" : isDeclined ? "Declined" : "Unknown"}
          </Badge>
        );
      },
    },
    {
      accessor: "",
      name: "Countdown",
      render: (offer: any) => {
        const createdAt = offer?.created_at;
        if (!createdAt) return <span className="text-gray-500">N/A</span>;

        const createdDate = new Date(createdAt);
        const expiryDate = new Date(createdDate);
        expiryDate.setDate(expiryDate.getDate() + 30);

        const today = new Date();
        const daysLeft = Math.ceil(
          (expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        );

        let textColor = "text-green-700";
        let bgColor = "bg-green-50";

        if (daysLeft <= 0) {
          textColor = "text-red-700";
          bgColor = "bg-red-50";
        } else if (daysLeft <= 7) {
          textColor = "text-orange-700";
          bgColor = "bg-orange-50";
        }

        return (
          <div
            className={`inline-flex items-center px-3 py-1 rounded-full ${bgColor}`}
          >
            <span className={`font-medium text-sm ${textColor}`}>
              {daysLeft > 0 ? `${daysLeft} days left` : "Expired"}
            </span>
          </div>
        );
      },
    },

    {
      accessor: "",
      name: "Action",
      render: (offer: any) => (
        <HoldOfferActions offer={offer} refresh={refresh} />
      ),
    },
  ];

  // Only include Action column if there are pending offers
  if (hasAnyPendingOffers) {
    return columns;
  }

  // Remove the Action column if all offers have decisions
  return columns.filter((col) => col.name !== "Action");
};
